import Flex from 'UIKit/Box/Flex'
import OpenEffect from 'UIKit/OpenEffect'
import Text from 'UIKit/Text'
import { ALL_GAME_TAG } from 'config/constants/gameTags'
import { useAnalytics } from 'hooks/useAnalytics'
import { useRequest } from 'hooks/useRequest'
import React, { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import GameService from 'services/GameService'
import styled from 'styled-components'
import PopularSearch from './PopularSearch'
import SearchHistory from './SearchHistory'
import SearchResult from './SearchResult'

type SearchContentProps = { searchText?: string; setSearchText: (text: string) => void }

const SearchContent: React.FC<SearchContentProps> = ({ searchText, setSearchText }) => {
  const [totalResult, setTotalResult] = useState(0)
  const [isFetchingTotalResult, setIsFetchingTotalResult] = useState(false)
  const [delayedSearchText, setDelayedSearchText] = useState('')
  const { recordEvent } = useAnalytics()
  const { execute } = useRequest()

  useEffect(() => {
    if (searchText.length >= 3) {
      setIsFetchingTotalResult(true)
      const fetch = async () => {
        const response = await execute(GameService.getGames({ tags: [ALL_GAME_TAG], query: searchText }, 0, 1))
        setTotalResult(response?.data?.paging?.total || 0)
        setIsFetchingTotalResult(false)
        setDelayedSearchText(searchText)
        if (!response?.data?.paging?.total) {
          recordEvent('search', {
            no_result: searchText,
          })
        }
      }
      fetch()
    } else {
      setDelayedSearchText(searchText)
    }
  }, [searchText])

  return (
    <StyledWrapper>
      {!totalResult && delayedSearchText.length >= 3 && !isFetchingTotalResult && (
        <OpenEffect openType="grow" mt={['6px', '6px', '18px']} padding={['0px 12px', '', '0px']} pb="12px !important">
          <Text color="text" fontSize="16px" bold>
            <Trans>No results for</Trans> “{searchText}”
          </Text>

          <Text color="textSubtle" fontSize="14px" bold mt="20px">
            <Trans>Tips for better search</Trans>:
          </Text>
          <ul>
            <Text as="li" color="textSubtle" fontSize="14px" mt="16px">
              <Trans>Insert at least 3 characters</Trans>
            </Text>
            <Text as="li" color="textSubtle" fontSize="14px" mt="4px">
              <Trans>Use fitter to help saving time searching</Trans>
            </Text>
            <Text as="li" color="textSubtle" fontSize="14px" mt="4px">
              <Trans>...</Trans>
            </Text>
          </ul>
        </OpenEffect>
      )}

      {!!totalResult && delayedSearchText.length >= 3 && (
        <Flex mt={['0px', '0px', '18px']} padding={['0px 12px', '', '0px']}>
          {isFetchingTotalResult ? (
            <Text color={'primary'} fontSize={['12px', '12px', '14px']} bold>
              ...
            </Text>
          ) : (
            <Text color={'primary'} fontSize={['12px', '12px', '14px']} bold>
              {totalResult}
            </Text>
          )}

          <Text ml="4px" color={'textSubtle'} fontSize={['12px', '12px', '14px']} bold>
            <Trans>Results for</Trans> “{delayedSearchText}”
          </Text>
        </Flex>
      )}

      {delayedSearchText.length < 3 && (
        <Flex justifyContent="center" alignItems="center" mt={['6px', '6px', '18px']} padding={['0px 12px', '', '0px']}>
          <Text textAlign="center" color={'textSubtle'} fontSize="12px">
            <Trans>Please enter 3 or more characters to search</Trans>
          </Text>
        </Flex>
      )}

      {(!totalResult || delayedSearchText.length < 3) && (
        <>
          <SearchHistory onChangeText={setSearchText} />
          <PopularSearch />
        </>
      )}

      {delayedSearchText.length >= 3 && totalResult > 0 && <SearchResult searchText={delayedSearchText} />}
    </StyledWrapper>
  )
}

const StyledWrapper = styled(Flex)`
  height: 100%;
  flex-direction: column;
`

export default React.memo(SearchContent)
